import { type Signal, signal } from '@preact/signals'

import type { Board } from '@/core/Board'
import type { GridCoords } from '@/core/Board/types'
import { sealed } from '@/decorators'
import { eventBus } from '@/libs/EventBus'
import { webSocketClient } from '@/libs/WebSocketClient'
import { assert } from '@/utils/assert'

import { type CityStructure, type CityZone, GRID_COLUMNS_LENGTH, GRID_LINES_LENGTH } from './constants'
import { GameError } from './libs/GameError'
import type { Coords, Grid, GridPatchData, GridPatchedData } from './types'

type PendingRect = [start: Coords, end: Coords]

@sealed
class Game {
  error: Signal<string | undefined>
  #_board: Board | undefined
  #_grid: Grid | undefined
  #isLoaded: boolean
  #pendingRects: Map<string, PendingRect>

  get #board(): Board {
    assert(this.#_board, '`this.#_board` is undefined.')

    return this.#_board
  }

  get #grid(): Grid {
    assert(this.#_grid, '`this.#_grid` is undefined.')

    return this.#_grid
  }

  constructor() {
    this.#isLoaded = false
    this.#pendingRects = new Map()
    this.error = signal(undefined)
  }

  async new(board: Board): Promise<void> {
    if (this.#isLoaded) {
      throw new GameError('`Game` is already loaded.')
    }

    this.#_board = board

    const { grid } = await webSocketClient.request<{ grid: Grid }>({ type: 'grid.get' })
    this.#assertGridDims(grid)
    this.#_grid = grid

    await this.#board.load()
    this.#board.render(this.#grid, { isNew: true })

    this.#isLoaded = true
  }

  patchGridAtLevel(start: GridCoords, end: GridCoords, level: number, zone: CityZone, structure: CityStructure) {
    const requestId = crypto.randomUUID()

    this.#pendingRects.set(requestId, [
      { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y) },
      { x: Math.max(start.x, end.x), y: Math.max(start.y, end.y) },
    ])
    this.#dispatchPendingChange()

    const data: GridPatchData = {
      end: { x: end.x, y: end.y },
      level,
      start: { x: start.x, y: start.y },
      structure,
      zone,
    }
    webSocketClient
      .request<GridPatchedData>({ data, requestId, type: 'grid.patch' })
      .then(patch => this.#commitPatch(requestId, patch))
      .catch(error => this.#rollbackPatch(requestId, error))
  }

  #assertGridDims(grid: Grid) {
    const firstRow = grid.at(0)
    if (grid.length !== GRID_LINES_LENGTH || firstRow?.length !== GRID_COLUMNS_LENGTH) {
      throw new GameError('The server grid has unexpected dimensions.')
    }
  }

  #commitPatch(requestId: string, patch: GridPatchedData) {
    this.#pendingRects.delete(requestId)
    this.#dispatchPendingChange()

    const nextGrid = this.#grid.map(row => row.slice())
    for (let y = patch.start.y; y <= patch.end.y; y++) {
      for (let x = patch.start.x; x <= patch.end.x; x++) {
        // biome-ignore lint/style/noNonNullAssertion: Server data was validated.
        nextGrid[y]![x]! = patch.tiles[y - patch.start.y]![x - patch.start.x]!
      }
    }
    this.#_grid = nextGrid

    this.#board.render(nextGrid, { isNew: true })
    this.error.value = undefined
  }

  #rollbackPatch(requestId: string, error: unknown) {
    this.#pendingRects.delete(requestId)
    this.#dispatchPendingChange()

    this.error.value = error instanceof Error ? error.message : String(error)
  }

  #dispatchPendingChange() {
    eventBus.GamePendingChange.dispatch([...this.#pendingRects.values()])
  }
}

export const game = new Game()
